import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import checkAuthenticated from 'portal-modules/common/utils/check-authenticated'
import routes from '../pages/routes'
import { AuthRedirect, AuthRoute } from 'portal-modules/auth/components'
import { FullscreenLoader } from 'portal-modules/common/components/loaders'
import { Helmet } from 'react-helmet'
import { PRIVATE_ROOT } from 'portal-modules/common/constants/urls'
import { Switch, Route } from 'react-router-dom'
import { UpdateLanguageModal } from 'portal-modules/common/components/modals'
import {
  authorizePartial,
  requestLeaseData,
} from 'portal-modules/auth/redux/modules/user'
import { connect } from 'react-redux'
import { getLocale } from 'portal-modules/common/utils/language-details'
import { hot } from 'react-hot-loader/root'
import { requestAccountSettings } from 'portal-modules/settings/redux/modules/accountSettings'
import { requestTenantProductPermissions } from 'portal-modules/common/redux/modules/productPermissions'
import { requestBalanceDue } from 'portal-modules/common/redux/modules/balanceDue'

class Root extends Component {
  componentDidMount() {
    const {
      authorizePartial,
      history,
      needsAccountSettings,
      needsLeaseData,
      needsPartialAuth,
      needsPermissions,
      needCurrencyCode,
      requestAccountSettings,
      requestLeaseData,
      requestTenantProductPermissions,
      authenticated,
      requestBalanceDue,
    } = this.props
    // for third-party logins, get property/lease data if needed
    if (needsLeaseData) requestLeaseData({ push: history.push })
    // get product permissions on boot if we have a valid user
    // but no permissions yet (on boot from localStorage, not after login)
    if (needsPermissions) {
      requestTenantProductPermissions()
    }
    if (needsPartialAuth) authorizePartial(history.push)
    // We need language preference set by user, which is returned by the get account settings API
    if (needsAccountSettings) requestAccountSettings()
    if (authenticated && needCurrencyCode) requestBalanceDue()
  }

  render() {
    const {
      authenticated,
      history,
      loadingMsg,
      preferredLocaleCode,
      currencyCode,
    } = this.props

    i18nUtils.setCurrency(currencyCode)
    // Check if language update prompt was cancelled in last page refresh
    // To avoid showing popup on next page refreshes
    let cancelled, updatedFromSettings
    if (typeof Storage !== 'undefined') {
      cancelled = !!localStorage.locale_update_cancelled
      updatedFromSettings = !!localStorage.language_updated_from_settings
    }

    return preferredLocaleCode &&
      authenticated &&
      !cancelled &&
      !updatedFromSettings &&
      preferredLocaleCode != getLocale().locale ? (
      <UpdateLanguageModal onClick={() => history.push(PRIVATE_ROOT)} />
    ) : (
      <Fragment>
        <Helmet>
          <title>Tenant Portal</title>
        </Helmet>
        <Switch>
          <AuthRedirect from='/' exact />
          {routes.map(({ restricted, ...route }) => {
            const Component = restricted ? AuthRoute : Route
            return <Component key={route.path} {...route} />
          })}
        </Switch>
        {/* Only show loader in desktop view */}
        {window.innerWidth >= 1025 && (
          <FullscreenLoader show={!!loadingMsg}>{loadingMsg}</FullscreenLoader>
        )}
      </Fragment>
    )
  }
}

Root.propTypes = {
  authenticated: PropTypes.bool,
  authorizePartial: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  loadingMsg: PropTypes.string,
  needsAccountSettings: PropTypes.bool,
  needsLeaseData: PropTypes.bool,
  needsPartialAuth: PropTypes.bool,
  needsPermissions: PropTypes.bool,
  preferredLocaleCode: PropTypes.string,
  requestAccountSettings: PropTypes.func.isRequired,
  requestLeaseData: PropTypes.func.isRequired,
  requestTenantProductPermissions: PropTypes.func.isRequired,
  requestBalanceDue: PropTypes.func.isRequired,
  needCurrencyCode: PropTypes.bool,
  currencyCode: PropTypes.string,
}

const ConnectedRoot = connect(
  ({
    accountSettings,
    leases,
    loading,
    productPermissions,
    properties,
    user,
    balanceDue,
  }) => {
    const authenticated = checkAuthenticated({ user, leases, properties })
    const needsLeaseData = user.token && !Object.keys(properties).length
    return {
      authenticated,
      loadingMsg: (loading.global && loading.global.message) || '',
      needsAccountSettings:
        authenticated && !accountSettings.language_preference,
      needsLeaseData,
      needsPartialAuth: user.token && !authenticated && !needsLeaseData,
      needsPermissions:
        authenticated && !Object.keys(productPermissions).length,
      preferredLocaleCode: accountSettings.language_preference,
      needCurrencyCode: !balanceDue || !balanceDue.currency_code,
      currencyCode: balanceDue?.currency_code || '',
    }
  },
  {
    authorizePartial,
    requestAccountSettings,
    requestLeaseData,
    requestTenantProductPermissions,
    requestBalanceDue,
  },
)(Root)

export default hot(ConnectedRoot)
